import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PaymentModel } from '@models/payment-model';
import { LocalizationService } from './localization.service';
import { PaymentStatusModel } from '@models/payment-status-model';
import { tap, map } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { PaymentTypes } from '@models/payment-types';
declare const paysafe: any;

interface MessageKey {
  key: 'CertificationPurchased' | 'GroupPurchased';
  params: any;
}
@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private apiUrl: string;

  private amount: number;
  private paymentType: PaymentTypes;
  private productId: string;
  private successCallback: Function;
  private successMessageKey: MessageKey;
  private singleUseToken: string;
  private environment: string;

  constructor(
    private http: HttpClient,
    @Inject('BASE_URL') private baseUrl: string,
    private locale: LocalizationService
  ) {
    this.resultCallback = this.resultCallback.bind(this);
    this.closeCallback = this.closeCallback.bind(this);
    this.openCheckout = this.openCheckout.bind(this);
    this.apiUrl = `${this.baseUrl}api/payment`;
  }

  getKey() {}

  postPayment(paymentModel: PaymentModel) {
    return this.http.post(`${this.apiUrl}`, paymentModel);
  }

  areBookingsComplete(userCertificationId: string) {
    return this.http.get<boolean>(`${this.apiUrl}/areBookingsComplete/${userCertificationId}`);
  }

  /**
   * Ping the Paysafe server to see if it is up and ready.
   * Since we also need to the single-use api key wait until that is retrieved as well before sending status back.
   */
  checkStatus() {
    const status = this.http.get(`${this.apiUrl}/status`).pipe(
      tap((res: any) => {
        if (res.status !== 'READY') {
          alert(
            'The payment system is temporarily out of service.  Try again later.'
          );
        }
      })
    );
    const key = this.http.get(`${this.apiUrl}/key`);

    return forkJoin([status, key]).pipe(
      tap((results: any[]) => {
        this.singleUseToken = results[1].key;
        this.environment = results[1].env;
      }),
      map(results => results[0])
    );
  }

  /**
   * Configures and opens the Paysafe checkout popup dialog.  This will appear as a modal overlay on the screen.
   * Documentation: https://developer.paysafe.com/en/sdks/paysafe-checkout/overview/
   * Test credit card number: 4111 1111 1111 1111
   * More Test card numbers at: https://developer.paysafe.com/en/rest-apis/cards/test-and-go-live/test-cards/
   * @param paymentType The type of payment that is being made.
   * @param id The ID of the thing that's being paid for.  This will be used differently depending on the paymentType.
   * @param amount The amount that will be charged to the user's credit card.
   * @param holderName The name that appears on the credit card.
   * @param successMessageKey The message details needed to display to correct message once payment is complete. Ex: {key: '[keyFromLocalFile]', params: {}}
   * @param successCallback A function to call once payment is complete and the user clicks the close button at the end.
   */
  async openCheckout(
    paymentType: PaymentTypes,
    id: string,
    amount: number,
    holderName: string,
    successMessageKey: MessageKey,
    successCallback?: Function
  ) {
    this.paymentType = paymentType;
    this.productId = id;
    this.successCallback = successCallback;
    this.successMessageKey = successMessageKey;
    this.amount = Math.round(amount * 100);

    // For Testing declined cards: https://developer.paysafe.com/en/rest-apis/cards/test-and-go-live/simulating-card-payments/
    // this.amount = parseInt(prompt('enter test amount')) || amount * 100;
    console.log('Paysafe environment:', this.environment);

    // We use en-CA and fr-CA, but Paysafe uses en_US and fr_CA.
    const locale = this.locale.getLocale().includes('fr') ? 'fr_CA' : 'en_US';

    // Options Documentation: https://developer.paysafe.com/en/sdks/paysafe-checkout/setup-function/#options
    const options = {
      holderName,
      locale, // Only options supported are: en_US, fr_CA
      amount: this.amount,
      currency: 'CAD',
      // Options are: TEST and LIVE.  Paysafe default is LIVE, but here, we'll default to TEST.
      environment: this.environment || 'TEST',
      companyName: 'KortoJura',
      imageUrl:
        'https://tks-kortojura-dev.azurewebsites.net/assets/images/kortojura_logo_full_small.png',
      buttonColor: '#9b3328',
      preferredPaymentMethod: 'Cards', //  Values: Cards, DirectDebit, Interac, PayPal
      displayPaymentMethods: ['Cards']
    };

    // Setup Documentation: https://developer.paysafe.com/en/sdks/paysafe-checkout/setup-function/#setup-function
    paysafe.checkout.setup(
      this.singleUseToken,
      options,
      this.resultCallback,
      this.closeCallback
    );
  }

  /**
   * Called once we've received a one-time token from Paysafe or there was an error opening the iframe.
   * @param paysafeOverlayInstance The instance of the Paysafe dialog popup
   * @param error Information at errors that have occurred.
   * @param result If all is good, this will contain the one-time token that will be used to complete the payment.
   */
  resultCallback(paysafeOverlayInstance, error, result) {
    // console.log('resultCallback', { paysafeOverlayInstance, result, error });
    const lk = 'RegisterCertification.Payment.';
    const lke = 'RegisterCertification.Payment.Errors.';
    if (!error && result && result.token) {
      if (result.paymentMethod === 'Cards') {
        // use AJAX to send result.token to your merchant server to take CC payment

        this.postPayment({
          token: result.token,
          amount: this.amount,
          paymentType: this.paymentType,
          productId: this.productId
        }).subscribe((res: PaymentStatusModel) => {
          if (res.status === 'COMPLETED') {
            let successMessage = this.locale.instant(
              `${lk}${this.successMessageKey.key}`,
              this.successMessageKey.params
            );
            successMessage += this.locale.instant(`${lk}PurchasedEndMessage`, {
              email: res.profile.email,
              authCode: res.authCode
            });
            paysafeOverlayInstance.showSuccessScreen(successMessage);
          } else {
            if (res.error.code) {
              // Max Paysafe error message length is 200 chars, so chop the email if it's too long.
              let errorMessage = this.locale.instant(`${lke}${res.error.code}`);

              if (errorMessage.includes('{{email}}')) {
                const email =
                  errorMessage.length + res.userEmail.length + 2 <= 200
                    ? `(${res.userEmail})`
                    : '';
                errorMessage = errorMessage.replace(/{{email}}/g, email);
              }

              paysafeOverlayInstance.showFailureScreen(
                errorMessage,
                this.locale.instant(`${lke}PurchaseErrorTitle`)
              );
            } else {
              paysafeOverlayInstance.showFailureScreen(
                this.locale.instant(`${lke}PurchaseError`),
                this.locale.instant(`${lke}PurchaseErrorTitle`)
              );
            }
          }
        });
      } else if (result.paymentMethod === 'DirectDebit') {
        // use AJAX to send result.token to your merchant server to take DD payment
      } else if (result.paymentMethod === 'Interac') {
        // use AJAX to send result.token to your merchant server to take Interac payment
      } else if (result.paymentMethod === 'PayPal') {
        // use AJAX to send result.token to your merchant server to take PayPal payment
      }
    } else {
      // error handling
      switch (error.code) {
        case '9064':
          // `Unable to launch the payment form.  Please try again later.\n\n${error.name} (${error.code})\n${error.detailedMessage}`
          alert(this.locale.instant(`${lke}Error9064`, error));
          break;
        default:
          if (paysafeOverlayInstance && paysafeOverlayInstance.isOpen()) {
            // `An error occurred while trying to make a payment.  No Payment was made. ${error.code}`,
            paysafeOverlayInstance.showFailureScreen(
              this.locale.instant(`${lke}Default`, error),
              this.locale.instant(`${lke}PaymentError`)
            );
          } else {
            alert(this.locale.instant(`${lke}DefaultAlert`, error));
          }
      }
    }
  }

  /**
   * Called if the user closes the checkout popup/overlay.
   * @param stage The stage at which the popup was closed.
   */
  closeCallback(stage: string) {
    if (stage === 'BeforePayment') {
      // No payment has been made.
      alert(
        this.locale.instant(
          `RegisterCertification.Payment.Errors.ClosedPrematurely`
        )
      );
    } else if (stage === 'DuringPayment') {
      // Token have been issued, but the checkout overlay was closed by the user before instance flow control method was invoked.
      alert(
        this.locale.instant(
          `RegisterCertification.Payment.Errors.ClosedPrematurely`
        )
      );
    } else if (stage === 'AfterPayment') {
      // Closed either via paysafeOverlayInstance.close method or by the user from the success screen.
      // This the normal flow.
      if (this.successCallback) {
        this.successCallback();
      }
    }
    this.successCallback = undefined;
  }
}
